(() => {
  'use strict';
  if (customElements.get('hh-sticky-atc')) return;
  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.root = document.querySelector('[data-hh-pdp][data-product-id="' + this.dataset.productId + '"]');
      if (!this.root) return;
      this.abort = new AbortController();
      this.description = this.querySelector('[data-sticky-description]');
      this.quantity = this.querySelector('[data-sticky-quantity]');
      this.minus = this.querySelector('[data-sticky-minus]');
      this.plus = this.querySelector('[data-sticky-plus]');
      this.submit = this.querySelector('[data-sticky-submit]');
      this.label = this.querySelector('[data-sticky-label]');
      this.mobile = matchMedia('(max-width:767px)');
      this.variants = this.readJson('[data-hh-pdp-variants]', []);
      this.packs = this.readJson('[data-hh-pdp-packs]', {});
      this.eligibleVariants = new Set((this.dataset.eligibleVariants || '').split(',').filter(Boolean));
      const on = (target, event, fn, options = {}) => target?.addEventListener(event, fn, {...options, signal:this.abort.signal});
      this.schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(() => {this.frame = 0; this.sync();}); };
      on(this.root, 'input', this.schedule);
      on(this.root, 'change', this.schedule);
      on(window, 'scroll', this.schedule, {passive:true});
      on(window, 'resize', this.schedule, {passive:true});
      on(window, 'pageshow', this.schedule);
      on(document, 'focusin', this.schedule);
      on(document, 'focusout', this.schedule);
      on(document, 'click', this.schedule);
      on(document, 'keydown', this.schedule);
      on(document, 'shopify:section:load', this.schedule);
      on(this.minus, 'click', () => this.step(-1));
      on(this.plus, 'click', () => this.step(1));
      on(this.querySelector('[data-sticky-pack]'), 'click', () => {
        const picker = this.root.querySelector('[data-hh-pdp-variant-pills]') || this.root.querySelector('[data-hh-pdp-variant-select]');
        picker?.scrollIntoView({block:'center',behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'instant':'smooth'});
        const current = picker?.querySelector('[aria-checked="true"]') || this.root.querySelector('[data-hh-pdp-variant-select]');
        current?.focus({preventScroll:true});
      });
      on(this.submit, 'click', () => {
        this.sync();
        if (this.submit.disabled || !this.nativeButton) return;
        this.nativeButton.click();
        this.schedule();
      });
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.root,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['disabled','aria-checked','hidden']});
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this);
      this.sync();
    }
    readJson(selector, fallback) {
      try {return JSON.parse(this.root.querySelector(selector)?.textContent || 'null') || fallback;} catch {return fallback;}
    }
    readState() {
      const select = this.root.querySelector('[data-hh-pdp-variant-select]');
      const id = select?.value;
      const variant = this.variants.find(v => String(v.id) === String(id));
      const input = this.root.querySelector('[data-hh-pdp-qty-input]');
      const compact = this.root.querySelector('[data-hh-pdp-qty-compact-select]');
      const usesOffers = compact && !compact.closest('[hidden]');
      const values = usesOffers ? [...compact.options].filter(o => !o.disabled).map(o => Number(o.value)).filter(n=>Number.isSafeInteger(n)&&n>0) : null;
      const qty = Number(input?.value || 1);
      return {id,variant,input,compact:usesOffers?compact:null,values,qty,pack:this.packs[id]};
    }
    step(direction) {
      const state = this.readState();
      if (!this.eligibleVariants.has(String(state.id)) || !state.variant?.available || this.nativeButton?.disabled || !state.input) return;
      if (state.compact) {
        const index = state.values.indexOf(state.qty);
        const next = state.values[index + direction];
        if (!next) return;
        state.compact.value = String(next);
        state.compact.dispatchEvent(new Event('change',{bubbles:true}));
      } else {
        const step = Number(state.input.step) || 1;
        const next = state.qty + direction * step;
        const min = Number(state.input.min) || 1;
        const max = state.input.max ? Number(state.input.max) : Infinity;
        if (!Number.isSafeInteger(next) || next < min || next > max) return;
        state.input.value = String(next);
        state.input.dispatchEvent(new Event('input',{bubbles:true}));
        state.input.dispatchEvent(new Event('change',{bubbles:true}));
      }
      this.sync();
    }
    money(cents) {
      return new Intl.NumberFormat('en-IN',{style:'currency',currency:this.root.dataset.currency||'INR',minimumFractionDigits:cents%100?2:0,maximumFractionDigits:2}).format(cents/100);
    }
    sync() {
      if (!this.isConnected || !this.root.isConnected) {this.disconnectedCallback(); return;}
      const state = this.readState();
      this.nativeButton = this.root.querySelector('[data-hh-pdp-atc]');
      const nativeLabel = this.root.querySelector('[data-hh-pdp-atc-label]');
      const valid = this.eligibleVariants.has(String(state.id)) && state.variant && state.pack?.approved && Number.isSafeInteger(state.qty) && state.qty>0;
      this.submit.disabled = !valid || !state.variant.available || !this.nativeButton || this.nativeButton.disabled;
      let text = nativeLabel?.textContent.trim() || '';
      if (valid && !state.compact && state.qty>1 && state.variant.available) text = text.replace(/·.*$/, '· ' + this.money(state.variant.price*state.qty));
      if (this.label.textContent !== text) this.label.textContent = text;
      const packCount = state.pack?.units_per_pack || state.pack?.capsules_per_pack;
      const variantTitle = state.variant?.title === 'Default Title' ? state.pack?.descriptor || this.root.querySelector('[data-hh-pdp-title]')?.textContent.trim() || '' : state.variant?.title || '';
      const packWords = packCount && state.pack?.unit_label_plural ? packCount + ' ' + state.pack.unit_label_plural : variantTitle;
      const offerEach = this.root.classList.contains('hh-pdp--clear-offers') ? this.root.querySelector('[data-purchase-tier][aria-checked="true"] .hh-purchase__unit-price')?.textContent.trim() : '';
      const desc = valid ? packWords + ' · ' + (offerEach || this.money(state.variant.price)) + ' ' + this.dataset.eachLabel : '';
      if (this.description.textContent !== desc) this.description.textContent = desc;
      this.quantity.value = String(state.qty);
      const index = state.values?.indexOf(state.qty);
      this.minus.disabled = this.submit.disabled || (state.values ? index<=0 : state.qty<=(Number(state.input?.min)||1));
      this.plus.disabled = this.submit.disabled || (state.values ? index<0||index>=state.values.length-1 : state.input?.max&&state.qty>=Number(state.input.max));
      const r = this.nativeButton?.getBoundingClientRect();
      const focused = document.activeElement;
      const typing = focused && !this.contains(focused) && focused.matches('input,textarea,select,[contenteditable="true"]');
      const openDialog = [...document.querySelectorAll('dialog[open],[role="dialog"][aria-modal="true"]')].some(d=>{
        const rect = d.getBoundingClientRect();
        const style = getComputedStyle(d);
        return d.getAttribute('aria-hidden')!=='true' && rect.width>0 && rect.height>0 && rect.left<innerWidth && rect.right>0 && rect.top<innerHeight && rect.bottom>0 && style.visibility!=='hidden' && style.display!=='none' && style.opacity!=='0';
      });
      this.hidden = !valid || !this.mobile.matches || !r || r.bottom>0 || typing || openDialog;
      document.body.classList.toggle('hh-sticky-atc-active',!this.hidden);
      if (!this.hidden) document.body.style.setProperty('--hh-sticky-atc-height',this.getBoundingClientRect().height+'px');
      else document.body.style.removeProperty('--hh-sticky-atc-height');
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      document.body.classList.remove('hh-sticky-atc-active');
      document.body.style.removeProperty('--hh-sticky-atc-height');
      this.abort = null;
    }
  }
  customElements.define('hh-sticky-atc', StickyAddToCart);
})();
